import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { DEFAULT_THEME, THEMES } from "../config";
import { RESUME_API } from "../helpers";
import { useCacheKey, useLocalStorage, useResumeData, useResumeEntries } from "../hooks";
import { SessionContext } from "./session-context";
export var NEW = "__NEW__";

var notImplemented = function notImplemented() {
  alert("Sorry, that feature is not available right now");
  return Promise.reject();
};

var createDefaults = function createDefaults() {
  var theme = THEMES.find(function (theme) {
    return theme.name === DEFAULT_THEME;
  });
  return {
    deleteResume: notImplemented,
    discardDraftResume: notImplemented,
    hasDraft: false,
    hasError: false,
    isInitialized: false,
    isLoaded: false,
    isLoading: false,
    isNewResume: false,
    isSaving: false,
    loadResume: notImplemented,
    newResume: notImplemented,
    resumeEntries: [],
    saveResume: notImplemented,
    saveResumeAs: notImplemented,
    storeDraftResume: notImplemented,
    setTheme: notImplemented,
    theme: theme,
    themes: THEMES,
    updateResumeEntry: notImplemented
  };
};

var createNewResume = function createNewResume() {
  return {
    certifications: [],
    contact_info: {},
    education: [],
    highlights: [],
    intro: "",
    skills: [],
    skill_groups: [],
    work_experience: []
  };
};

export var ResumeDataContext = /*#__PURE__*/createContext(createDefaults());
export var WithResumeDataContext = function WithResumeDataContext(_ref) {
  var children = _ref.children;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isInitialized = _useState2[0],
      setIsInitialized = _useState2[1];

  var sessionUser = useContext(SessionContext);
  var isLoggedIn = sessionUser !== null;

  var _useCacheKey = useCacheKey(),
      cacheKey = _useCacheKey.cacheKey,
      generateNewKey = _useCacheKey.generateNewKey;

  var _useResumeEntries = useResumeEntries(cacheKey),
      _useResumeEntries$dat = _useResumeEntries.data,
      resumeEntries = _useResumeEntries$dat === void 0 ? [] : _useResumeEntries$dat,
      resumeEntriesError = _useResumeEntries.error,
      resumeEntriesLoaded = _useResumeEntries.isLoaded,
      resumeEntriesLoading = _useResumeEntries.isLoading;

  var _useState3 = useState(),
      _useState4 = _slicedToArray(_useState3, 2),
      resumeId = _useState4[0],
      setResumeId = _useState4[1];

  var isNewResume = resumeId === NEW;
  var resumeEntry = isNewResume ? undefined : resumeEntries === null || resumeEntries === void 0 ? void 0 : resumeEntries.find(function (_ref2) {
    var id = _ref2.id;
    return id === resumeId;
  });

  var _useResumeData = useResumeData(isNewResume ? undefined : resumeId, "edit", cacheKey),
      resumeIsLoaded = _useResumeData.isLoaded,
      resumeIsLoading = _useResumeData.isLoading,
      resumeError = _useResumeData.error,
      currentResume = _useResumeData.data;

  var _useLocalStorage = useLocalStorage("draft"),
      draftResume = _useLocalStorage.value,
      storeDraftResume = _useLocalStorage.setValue,
      discardDraftResume = _useLocalStorage.clearValue;

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isSaving = _useState6[0],
      setIsSaving = _useState6[1];

  var hasDraft = !!draftResume;
  var isLoaded = resumeEntriesLoaded && (!resumeId || !isNewResume || resumeId && resumeIsLoaded);
  var isLoading = resumeEntriesLoading || !!resumeId && !isNewResume && resumeIsLoading;
  var error = resumeEntriesError || resumeError;
  var hasError = !!error; // TODO store draft of resumeEntry

  var theme = THEMES.find(function (t) {
    return t.name === (resumeEntry === null || resumeEntry === void 0 ? void 0 : resumeEntry.theme);
  });
  useEffect(function () {
    if (isLoggedIn && isInitialized) return;

    if (!isLoggedIn && isInitialized) {
      discardDraftResume();
      setIsInitialized(false);
      return;
    }

    if (!isLoggedIn) return; // logged in and not initialized

    if (resumeEntriesLoaded) {
      setIsInitialized(true);
    }
  }, [isLoggedIn, discardDraftResume, isInitialized, resumeEntriesLoaded]);
  var newResume = useCallback(function () {
    setResumeId(NEW);
    storeDraftResume(createNewResume());
  }, [storeDraftResume]);
  var loadResume = useCallback(function (loadResumeId) {
    setResumeId(loadResumeId);
    return Promise.resolve(true);
  }, []);
  var saveResumeAs = useCallback( /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(withName, asNew) {
      var resume, payload, id;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              setIsSaving(true);
              resume = draftResume !== null && draftResume !== void 0 ? draftResume : currentResume === null || currentResume === void 0 ? void 0 : currentResume.resume;
              payload = _objectSpread(_objectSpread({}, resumeEntry), {}, {
                resume: resume,
                id: asNew ? NEW : resumeId !== null && resumeId !== void 0 ? resumeId : NEW,
                userId: sessionUser.id
              }, withName && {
                displayName: withName
              });
              _context.next = 5;
              return RESUME_API.Save(resumeId !== null && resumeId !== void 0 ? resumeId : NEW, payload);

            case 5:
              id = _context.sent;
              discardDraftResume();
              setIsSaving(false);
              generateNewKey();
              toast.success("Save succeeded!");

              if (!(id !== resumeId)) {
                _context.next = 13;
                break;
              }

              _context.next = 13;
              return loadResume(id);

            case 13:
              return _context.abrupt("return", id);

            case 14:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function (_x, _x2) {
      return _ref3.apply(this, arguments);
    };
  }(), [currentResume, discardDraftResume, draftResume, generateNewKey, loadResume, resumeEntry, resumeId, sessionUser]);
  var saveResume = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 2;
            return saveResumeAs();

          case 2:
            return _context2.abrupt("return", _context2.sent);

          case 3:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  })), [saveResumeAs]);
  var updateResumeEntry = useCallback( /*#__PURE__*/function () {
    var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(resumeId, resumeEntryPayload) {
      var result;
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              setIsSaving(true);
              _context3.next = 3;
              return RESUME_API.UpdateEntry(resumeId, resumeEntryPayload);

            case 3:
              result = _context3.sent;
              setIsSaving(false);
              generateNewKey();
              toast.success("Update succeeded!");
              return _context3.abrupt("return", result);

            case 8:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3);
    }));

    return function (_x3, _x4) {
      return _ref5.apply(this, arguments);
    };
  }(), [generateNewKey]);
  var deleteResume = useCallback( /*#__PURE__*/function () {
    var _ref6 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(resumeId) {
      var result;
      return _regeneratorRuntime.wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              setIsSaving(true);
              _context4.next = 3;
              return RESUME_API.Delete(resumeId);

            case 3:
              result = _context4.sent;
              setIsSaving(false);
              generateNewKey();
              toast.success("Delete succeeded!");
              return _context4.abrupt("return", result);

            case 8:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4);
    }));

    return function (_x5) {
      return _ref6.apply(this, arguments);
    };
  }(), [generateNewKey]);

  var currentContext = _objectSpread(_objectSpread({}, createDefaults()), {}, {
    deleteResume: deleteResume,
    discardDraftResume: discardDraftResume,
    draftResume: draftResume,
    error: error,
    hasDraft: hasDraft,
    hasError: hasError,
    isInitialized: isInitialized,
    isLoaded: isLoaded,
    isLoading: isLoading,
    isNewResume: isNewResume,
    isSaving: isSaving,
    loadResume: loadResume,
    newResume: newResume,
    resume: currentResume === null || currentResume === void 0 ? void 0 : currentResume.resume,
    resumeEntries: resumeEntries !== null && resumeEntries !== void 0 ? resumeEntries : [],
    resumeId: resumeId,
    saveResume: saveResume,
    saveResumeAs: saveResumeAs,
    storeDraftResume: storeDraftResume,
    theme: theme,
    updateResumeEntry: updateResumeEntry
  });

  console.log(currentContext);
  return /*#__PURE__*/React.createElement(ResumeDataContext.Provider, {
    value: currentContext
  }, children);
};
export var useResumeDataContext = function useResumeDataContext() {
  var context = useContext(ResumeDataContext);
  return context;
};